import React, { useEffect } from "react";

import getRegionComplianceRules from "@/api/requests/consumerApi/getRegionComplianceRules";
import { getDispensary } from "@/api/requests/getDispensary";
import { getDispensaryDeals } from "@/api/requests/getDispensaryDeals";
import { DISPENSARY_PAGES } from "@/constants/dispensary";
import { Deal } from "@/custom-types/Deals";
import { Dispensary, RetailType } from "@/custom-types/Dispensary";
import useDomainCountryCode from "@/hooks/useDomainCountryCode";
import { retailerDealsMetaData } from "@/lib/retailerMetadata";
import { useDispatch } from "@/redux/hooks";
import {
  ComplianceRuleset,
  loadComplianceRuleset,
} from "@/redux/slices/complianceRules";
import { createGetServerSideProps } from "@/utils/createGetServerSideProps";
import generateDispensaryRedirect from "@/utils/generateDispensaryRedirect";
import { throwOnRedirectHint } from "@/utils/redirectHints";
import { getCountryCodeHeader } from "@/utils/requestContext";

import DealsDisclaimer from "@/components/Dispensary/DealsDisclaimer";
import DealsSection from "@/components/Dispensary/DealsSection";
import DispensaryBreadcrumbs from "@/components/Dispensary/DispensaryBreadcrumbs";
import DispensaryHeader from "@/components/Dispensary/DispensaryHeader";
import DispensaryTabs from "@/components/Dispensary/DispensaryTabs";
import PromotionsSection from "@/components/Dispensary/PromotionsSection";
import MetaTags from "@/components/MetaTags";

type DealsPageProps = {
  complianceRuleset: ComplianceRuleset;
  dispensary: Dispensary;
  deals: Deal[];
  totalDealsCount: number;
};

const DealsPage: React.FC<DealsPageProps> = ({
  complianceRuleset,
  dispensary,
  deals,
  totalDealsCount,
}) => {
  const dispatch = useDispatch();
  const countryCode = useDomainCountryCode();

  const { coverPhotoUrl, logoUrl, name, retailType } = dispensary;

  const { description, image, title } = retailerDealsMetaData(
    countryCode,
    coverPhotoUrl,
    logoUrl,
    name,
    retailType as RetailType,
  );

  useEffect(() => {
    if (complianceRuleset) {
      dispatch(loadComplianceRuleset(complianceRuleset));
    }
  }, []);

  return (
    <>
      <MetaTags
        description={description}
        image={image}
        shouldShowAlternates={false}
        title={title}
      />
      <DispensaryHeader dispensary={dispensary} />
      <DispensaryTabs
        activeTab={DISPENSARY_PAGES.deals}
        dispensary={dispensary}
      />

      <div data-testid="deals__content" className="container pt-xl pb-xxl">
        <DealsSection
          initialDeals={deals}
          dispensary={dispensary}
          totalDealsCount={totalDealsCount}
        />
        <PromotionsSection
          timeZone={dispensary.timeZone}
          name={dispensary.name}
          id={dispensary.id}
          title="Promotions"
          subtitle=" Know before you go! These promos are available to claim in-store. Ask
          your budtender before you checkout!"
        />
        <DealsDisclaimer />
        <DispensaryBreadcrumbs dispensary={dispensary} />
      </div>
    </>
  );
};

export const getServerSideProps = createGetServerSideProps<DealsPageProps>(
  async (context) => {
    const { query, req, resolvedUrl } = context;
    const { slug } = query;

    const dispensary = await throwOnRedirectHint(
      () => getDispensary(slug as string),
      slug as string,
    );

    const domainCountryCode = getCountryCodeHeader(req.headers);

    const redirect = generateDispensaryRedirect(
      domainCountryCode,
      resolvedUrl,
      query,
      dispensary,
    );

    if (redirect) {
      return { redirect };
    }

    // fallback if redirect not generated
    if (!dispensary) {
      return {
        notFound: true,
      };
    }

    const [{ data: deals, metadata }, complianceRuleset] = await Promise.all([
      getDispensaryDeals(String(slug), 21, 0),
      getRegionComplianceRules(dispensary.state, dispensary.country),
    ]);

    return {
      props: {
        complianceRuleset,
        deals,
        dispensary,
        totalDealsCount: metadata.totalCount,
      },
    };
  },
);

export default DealsPage;
